import { format } from 'date-fns';

const DATE_PREFIX_PATTERN = /^(\d{4}-\d{2}-\d{2})/;

export const parseLegacyISO = (value: string): string | null => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  const datePrefixMatch = trimmedValue.match(DATE_PREFIX_PATTERN);
  return datePrefixMatch ? datePrefixMatch[1] : null;
};

export const normalizeDate = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null;
    }

    return normalizeDate(value[0]);
  }

  if (typeof value === 'string') {
    const legacyDate = parseLegacyISO(value);
    if (legacyDate) {
      return legacyDate;
    }

    const parsedDate = new Date(value.trim());
    if (!Number.isNaN(parsedDate.getTime())) {
      return format(parsedDate, 'yyyy-MM-dd');
    }

    return null;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return format(value, 'yyyy-MM-dd');
  }

  return null;
};

export const toDateFilterString = normalizeDate;

export const formatLocalDateString = (date: Date): string => format(date, 'yyyy-MM-dd');

export const buildFullDayRange = (fromDate: string, toDate: string): [string, string] => [
  `${fromDate}T00:00:00`,
  `${toDate}T23:59:59`,
];

export const buildDateFilterDateTimeRange = buildFullDayRange;

export const buildSingleDateFilterRange = (date: string): [string, string] => (
  buildFullDayRange(date, date)
);

export const toLocalMidnightDate = (value: string | null | undefined): Date | null => {
  const normalizedDate = normalizeDate(value);
  return normalizedDate ? new Date(`${normalizedDate}T00:00:00`) : null;
};

type DefaultTodayFilterShape = {
  timePeriod?: string | null;
  date?: unknown;
  fromDate?: unknown;
  toDate?: unknown;
  fromTime?: string | null;
  toTime?: string | null;
};

export const shouldApplyDefaultToday = (
  filters: DefaultTodayFilterShape,
  noFilterValue = 'No filter',
): boolean => {
  const hasDateRelatedFilters = Boolean(
    (filters.timePeriod && filters.timePeriod !== noFilterValue)
    || filters.date
    || filters.fromDate
    || filters.toDate
    || filters.fromTime
    || filters.toTime
  );

  return filters.timePeriod === noFilterValue || !hasDateRelatedFilters;
};
